import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqs } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

export default function FAQSection() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const { ref, isInView } = useInView();

  const toggleFaq = (id: number) => {
    setActiveId(activeId === id ? null : id);
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="px-4 md:px-6">
        <div
          ref={ref}
          className={`max-w-[1200px] mx-auto transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="lg:w-[35%] flex-shrink-0">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                <i className="ri-question-answer-line mr-1"></i>常见问题
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading leading-tight">
                关于
                <span className="text-primary-600">韩漫网</span>
                <br />
                你可能会问
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                整理了读者最常关心的问题，帮助你更好地使用<strong>韩漫在线</strong>平台。
                如果还有其他疑问，欢迎随时<Link to="/about" className="text-primary-600 underline">联系我们</Link>。
              </p>
              <div className="mt-8 hidden lg:block">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20minimalist%20illustration%20of%20a%20person%20comfortably%20reading%20a%20digital%20comic%20on%20a%20tablet%20in%20a%20cozy%20environment%2C%20warm%20ambient%20lighting%2C%20clean%20flat%20vector%20style%2C%20modern%20illustration%2C%20calm%20mood%2C%20neutral%20colors%2C%20simple%20composition&width=500&height=400&seq=faq-illustration&orientation=landscape"
                    alt="韩漫在线阅读场景"
                    className="w-full h-auto rounded-xl"
                    title="韩漫在线 - 舒适阅读体验"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden transition-all duration-300"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={activeId === faq.id}
                    >
                      <h3 className="text-sm font-semibold text-foreground-900 pr-4 font-heading">
                        {faq.question}
                      </h3>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        activeId === faq.id ? 'bg-primary-500 text-background-50 rotate-180' : 'bg-background-200 text-foreground-500'
                      }`}>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        activeId === faq.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}